import { FieldArray } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useCallback, useState } from 'react';

import { DECISION_RESULT } from 'CorePackage/MainModule/routes/routes';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { KnmResultDecisionAgreementStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionAgreementStatusEnum';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';

import {
  IDecisionResultDecisionForm,
  IKnmDecisionSubjectTypeSelectOption,
} from '../../MainModule/interfaces/IDecisionResultDecisionForm';
import { IDecisionFormRef } from '../../MainModule/interfaces/IDecisionFormRef';
import {
  getDecisionExternalParams,
  getDecisionFormRef,
  getKnmIsDecisionStatusTypeCompleted,
} from '../../MainModule/selectors/decisionSelectors';
import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IKnmErknmFormViolatedRequirementsNpa } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { DecisionResult } from './DecisionResult';
import { DECISION_RESULT_BLOCK_ID } from '../../MainModule/constants/decisionFieldsElementsIds';
import { getResultDecisionBlockId } from '../../../KnmPackage/MainModule/constants/fieldsElementsIds';
import styles from './DecisionResult.module.scss';
import { ResultDecisionStatus } from '../../../KnmErknmPackage/ErknmResultDecisionsModule/components/ResultDecisionStatus';
import { KindResultDecisionCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IDecisionResultsProps {
  violatedRequirementsNpa: IKnmErknmFormViolatedRequirementsNpa[] | undefined;
  title: string;
  isWatchOnly: boolean;
  organization: IDecisionOrganizationForm | undefined;
  agreements: IErknmFormAgreement[] | undefined;
  isLater4111ForDecision?: boolean;
  isEnablePaperlessDocument?: boolean;
}

interface IDecisionResultsStoreProps {
  formRef: IDecisionFormRef | undefined;
  isSupervisor: boolean;
  isDecisionStatusCompleted: boolean;
  decisionExternalParams?: Record<string, string>;
}

export const resultDecisionSubjectTypeOptions: IKnmDecisionSubjectTypeSelectOption[] = [
  {
    id: KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT,
    value: KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT,
    label: 'Контролируемое лицо',
  },
  {
    id: KnmResultDecisionSubjectTypesEnum.FOUNDER,
    value: KnmResultDecisionSubjectTypesEnum.FOUNDER,
    label: 'Учредитель',
  },
];

function DecisionResultsWithoutStore({
  formRef,
  isWatchOnly,
  violatedRequirementsNpa,
  title,
  isSupervisor,
  isDecisionStatusCompleted,
  organization,
  agreements,
  isLater4111ForDecision,
  isEnablePaperlessDocument,
  decisionExternalParams,
}: IDecisionResultsProps & IDecisionResultsStoreProps) {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const resultDecisionsPath = 'organization.resultDecisions';

  const addResultDecision = useCallback(
    () =>
      !isWatchOnly &&
      push &&
      push(resultDecisionsPath, {
        isNew: true,
        listId: uuidv4(),
        subjectType: resultDecisionSubjectTypeOptions[0],
      } as IDecisionResultDecisionForm),
    [push, isWatchOnly],
  );

  const isDisable =
    !isSupervisor ||
    !isDecisionStatusCompleted ||
    isWatchOnly ||
    !!organization?.resultDecisions?.find(rd => rd.isNew) ||
    !!organization?.act?.fileInvalidate;

  const isExpandAllInPrescription = decisionExternalParams?.expandAllInPrescription === 'true';

  const makeRemoveResultDecisionHandler = (index: number) => () => {
    if (!isWatchOnly && remove) {
      remove(resultDecisionsPath, index);
    }
  };

  /**
   * Функция определения дополнительного статуса по соглашению
   */
  const getAgreementStatus = useCallback(
    (value: IDecisionResultDecisionForm | undefined) => {
      if (agreements) {
        const agreement = agreements.find(agr => agr.prescription?.id === value?.guid && agr.isNew);

        if (agreement) return KnmResultDecisionAgreementStatusEnum.AGREEMENT_DRAFT;
      }

      return value?.agreementStatus;
    },
    [agreements],
  );

  return (
    <Section
      title={title}
      id={DECISION_RESULT}
      titleId={DECISION_RESULT_BLOCK_ID}
      type="list"
      customButton={
        <>
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={isDisable}
            onClick={addResultDecision}
          />
        </>
      }
    >
      <FieldArray name={resultDecisionsPath}>
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((name, index) => {
                const fieldValue: IDecisionResultDecisionForm = fields.value?.[index];
                const id = getResultDecisionBlockId(fieldValue.listId || fieldValue.guid);
                const isPrescription = fields.value?.[index]?.kind?.code === KindResultDecisionCodesEnum.D_7;

                const hasError = checkHasErrorInCollapseBlock(formRef, name);

                return (
                  <KnmCollapse
                    theme="fields"
                    title={fieldValue.paperless || fieldValue.isNew ? 'Решение' : `Решение №${fieldValue.number || ''}`}
                    subtitle={fieldValue.paperless ? fieldValue.number : ''}
                    id={id}
                    key={id}
                    isNew={!fieldValue.paperless ? fieldValue.isNew : false}
                    isNewTitle={'Новое'}
                    opened={fieldValue.isNew}
                    openByDefault={isExpandAllInPrescription}
                    onClose={fieldValue.isNew ? makeRemoveResultDecisionHandler(index) : undefined}
                    headerPostfix={
                      isPrescription ? (
                        <div className={styles.DecisionStatusWrapper}>
                          <ResultDecisionStatus
                            status={fieldValue.status || undefined}
                            isNew={fieldValue.isNew}
                            agreementStatus={getAgreementStatus(fieldValue)}
                            isPaperless={!!fieldValue.paperless}
                          />
                        </div>
                      ) : undefined
                    }
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [name]: e,
                      }))
                    }
                    error={hasError && knmCollapseOpened[name]}
                  >
                    <Row>
                      <DecisionResult
                        name={name}
                        isWatchOnly={isWatchOnly}
                        resultDecision={fieldValue}
                        formRef={formRef}
                        violatedRequirementsNpa={violatedRequirementsNpa}
                        resultDecisions={fields.value}
                        resultDecisionIndex={index}
                        organization={organization}
                        resultDecisionId={id}
                        isLater4111ForDecision={isLater4111ForDecision}
                        isEnablePaperlessDocument={isEnablePaperlessDocument}
                        isExpandAllInPrescription={isExpandAllInPrescription}
                      />
                    </Row>
                  </KnmCollapse>
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </Section>
  );
}

const mapStateToProps = (state: IRootState): IDecisionResultsStoreProps => ({
  formRef: getDecisionFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isDecisionStatusCompleted: getKnmIsDecisionStatusTypeCompleted(state),
  decisionExternalParams: getDecisionExternalParams(state),
});

export const DecisionResults = connect(mapStateToProps)(DecisionResultsWithoutStore);
